import type { MovementId } from '../config/movements';
import { getMovement } from '../config/movements';
import type { KitGroup, WizardLine, WizardVariant } from '../types/wizard';
import type { RfidDocumentRow } from '../services/rfid.service';
import { detectWizardVariant, getDocumentItems } from './document';

function normalizeItemArray(raw: unknown): Record<string, unknown>[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  return [raw as Record<string, unknown>];
}

function componentList(item: Record<string, unknown>): Record<string, unknown>[] {
  const comps = item.Components as { Component?: unknown; Item?: unknown } | undefined;
  if (!comps) return [];
  const raw = comps.Component ?? comps.Item ?? comps;
  return normalizeItemArray(raw);
}

function itemHasKitComponents(item: Record<string, unknown>): boolean {
  return componentList(item).length > 0;
}

/** Map transfer-out kit/item API rows into wizard lines (legacy Get_ItemDetails). */
export function buildLinesFromTransferItems(
  items: Record<string, unknown>[],
  movementId: MovementId
): { lines: WizardLine[]; kitGroups: KitGroup[]; variant: WizardVariant } {
  const m = getMovement(movementId);
  const kitItems = items.filter(itemHasKitComponents);

  if (kitItems.length > 0) {
    const kitGroups: KitGroup[] = kitItems.map((item, ki) => {
      const compList = componentList(item);
      const kitQty = parseInt(String(item.Quantity ?? item.KitQty ?? '1'), 10) || 1;
      const kitMaterial = String(item.Material ?? item.Material_Code ?? item.ItemCode ?? '');
      const headerLine: WizardLine = {
        id: `kit-${ki}`,
        itemNo: String(item.ItemNo ?? (ki + 1) * 10),
        materialCode: kitMaterial,
        materialDesc: String(item.MaterialDesc ?? item.Description ?? ''),
        quantity: kitQty,
        scannable: false,
        scanQty: 0,
        nonScanQty: null,
        shortQty: 0,
        fromBay: '',
        fromBin: m.fromBin ?? 'CONDITION',
        toBay: '',
        toBin: '',
        sender: '',
        senderName: '',
        rfidCodes: [],
        isKitHeader: true,
        kitQty,
      };
      const lines = compList.map((c, ci) => lineFromTransferComponent(c, movementId, ci, ki));
      return {
        kitMaterial,
        kitDesc: String(item.MaterialDesc ?? item.Description ?? ''),
        kitQty,
        sender: '',
        senderName: '',
        lines: [headerLine, ...lines],
      };
    });
    return { lines: kitGroups.flatMap((g) => g.lines), kitGroups, variant: 'kit' };
  }

  const lines = items.map((item, i) => lineFromTransferComponent(item, movementId, i, 0));
  return { lines, kitGroups: [], variant: 'flat' };
}

function lineFromTransferComponent(
  item: Record<string, unknown>,
  movementId: MovementId,
  idx: number,
  kitIndex: number
): WizardLine {
  const m = getMovement(movementId);
  const qty = parseInt(String(item.Quantity ?? item.Qty ?? '0'), 10) || 0;
  const scannable = String(item.Scannable ?? '') === 'X';
  return {
    id: `tr-${kitIndex}-${item.ItemNo ?? idx}-${item.Material ?? item.Component ?? idx}`,
    itemNo: String(item.ItemNo ?? item.ITEMNO ?? (idx + 1) * 10),
    materialCode: String(item.Material ?? item.Component ?? item.Material_Code ?? item.ITEMCODE ?? ''),
    materialDesc: String(
      item.MaterialDesc ?? item.Description ?? item.ComponentDesc ?? item.MATERIAL_DESC ?? ''
    ),
    quantity: qty,
    scannable,
    scanQty: 0,
    nonScanQty: null,
    shortQty: 0,
    fromBay: String(item.FromBay ?? ''),
    fromBin: m.fromBin ?? String(item.FromBin ?? ''),
    toBay: m.toBay ?? String(item.ToBay ?? item.TO_BAY ?? ''),
    toBin: m.toBin ?? String(item.ToBin ?? item.TO_BIN ?? ''),
    sender: String(item.Sender ?? item.Plant ?? ''),
    senderName: String(item.Sender_Name ?? ''),
    rfidCodes: [],
  };
}

/** Normalize TRIN base document (Get_BaseDoc) into wizard document shape. */
export function baseDocToWizardDocument(
  raw: Record<string, unknown>,
  customerName: string
): RfidDocumentRow {
  const itemsRaw = raw.ITEMS as { item?: unknown } | undefined;
  const itemList = normalizeItemArray(itemsRaw?.item);
  const mappedItems = itemList.map((row) => ({
    ItemNo: row.ITEMNO ?? row.ItemNo,
    Material: row.ITEMCODE ?? row.Material,
    MaterialDesc: row.MATERIAL_DESC ?? row.MaterialDesc,
    Quantity: row.DISP_REC_QTY ?? row.Quantity,
    Scannable: row.Scannable ?? 'X',
    FromBin: row.FROM_BIN,
    ToBay: row.TO_BAY,
    ToBin: row.TO_BIN,
    Sender: raw.SENDER,
    Plant: row.FROM_WH ?? raw.SENDER,
  }));

  return {
    DocumentNo: String(raw.DOCUMENT_NO ?? raw.DocumentNo ?? ''),
    PostingDate: String(raw.EFFECTIVE_DATE ?? raw.PostingDate ?? ''),
    CustName: customerName,
    Sender: String(raw.SENDER ?? ''),
    Receiver: String(raw.RECEIVER ?? raw.RECIEVER ?? ''),
    TransporterCode: String(raw.TRANSPORTER_CODE ?? ''),
    VehicleNo: String(raw.VEHICLE_NO ?? ''),
    VehicleType: String(raw.VEHICLE_TYPE ?? ''),
    DriverName: String(raw.DRIVER_NAME ?? ''),
    DriverMobileNo: String(raw.DRIVER_MOB_NO ?? ''),
    InspectedBy: String(raw.INSPECTEDBY ?? ''),
    LR: String(raw.LR_NO ?? ''),
    ModeofTransport: String(raw.TRANSPORT_MODE ?? ''),
    Items: { Item: mappedItems },
  };
}

export function transferItemsHaveKits(items: Record<string, unknown>[]): boolean {
  return items.some(itemHasKitComponents);
}

export function summarizeTransferBaseDoc(doc: RfidDocumentRow): string {
  const items = getDocumentItems(doc);
  const variant = detectWizardVariant(doc);
  return `${String(doc.DocumentNo ?? '—')} · ${items.length} line${items.length === 1 ? '' : 's'} · ${variant === 'kit' ? 'Kit' : 'Flat'}`;
}
